import { get } from '@/services/http';
import {
  ExcludedItemAPIInterface,
  ExcludedItemInterface,
} from '@/models/inventory_transfer_suggestion';
import type { ProductFilterCondition } from '@/collections/inventory_transfer_suggestion_items';

export interface GetExcludedItemsParams {
  fromLocationId: number;
  toLocationId: number;
  page?: number;
  perPage?: number;
  reason?: string[]; // CSV server-side
  search?: string;
  // Applied server-side in the OLAP query so the excluded panel matches the
  // product-filtered suggestions next to it.
  productFilters?: ProductFilterCondition[];
}

export interface ExcludedItemsResponse {
  items: ExcludedItemInterface[];
  total: number;
  page: number;
  perPage: number;
}

interface RawResponse {
  data: {
    items: ExcludedItemAPIInterface[];
    total: number;
    page: number;
    per_page: number;
  };
}

const mapItem = (raw: ExcludedItemAPIInterface): ExcludedItemInterface => ({
  productTitle: raw.product_title ?? '',
  variantTitle: raw.variant_title ?? '',
  sku: raw.sku ?? '',
  variantId: raw.variant_id ?? '',
  exclusionReason: raw.exclusion_reason ?? '',
  reasonLabel: raw.reason_label ?? '',
  reasonDetail: raw.reason_detail ?? '',
  sourceInventory: raw.source_inventory ?? 0,
  destinationInventory: raw.destination_inventory ?? 0,
  reorderPoint: raw.reorder_point ?? 0,
  sourceTransferableQuantity: raw.source_transferable_quantity ?? 0,
  pendingTransferQuantity: raw.pending_transfer_quantity ?? 0,
});

export class InventoryTransferExcludedItems {
  public static async fetch(
    params: GetExcludedItemsParams,
  ): Promise<ExcludedItemsResponse> {
    const query: Record<string, any> = {
      from_location_id: params.fromLocationId,
      to_location_id: params.toLocationId,
      page: params.page ?? 1,
      per_page: params.perPage ?? 20,
    };
    if (params.reason && params.reason.length > 0) {
      query.reason = params.reason.join(',');
    }
    if (params.search && params.search.trim()) {
      query.search = params.search.trim();
    }
    if (params.productFilters && params.productFilters.length > 0) {
      query.product_filters = JSON.stringify(params.productFilters);
    }

    const response: RawResponse = await get('/inventory-transfers/excluded-items', query);
    return {
      items: response.data.items.map(mapItem),
      total: response.data.total,
      page: response.data.page,
      perPage: response.data.per_page,
    };
  }
}
